import { apiClient } from './apiClient';

// -------------------------------------------------------------------------
// Types
// -------------------------------------------------------------------------

export interface VoicemailDropRecording {
  id: string;
  tenantId: string;
  userId: string;
  name: string;
  audioUrl: string | null;
  durationSeconds: number | null;
  isActive: boolean;
}

export interface TenantDidPool {
  id: string;
  tenantId: string;
  countryCode: string;
  phoneNumber: string;
  provider: string;
  isActive: boolean;
}

export interface VoicemailDropResult {
  success: boolean;
  error: string | null;
}

export interface TransferToHumanResult {
  success: boolean;
  error: string | null;
  transcriptSnippet: string | null;
}

export interface SessionTranscriptLine {
  speaker: string;
  text: string;
  startedAt: string;
}

export interface SessionTranscript {
  sessionId: string;
  lines: SessionTranscriptLine[];
}

// -------------------------------------------------------------------------
// Voicemail drops
// -------------------------------------------------------------------------

export async function fetchVoicemailDrops(): Promise<VoicemailDropRecording[]> {
  const { data } = await apiClient.get<VoicemailDropRecording[]>('/dialer/voicemail-drops');
  return data;
}

export async function saveVoicemailDrop(
  name: string,
  audioUrl: string,
  durationSeconds?: number,
): Promise<VoicemailDropRecording> {
  const { data } = await apiClient.post<VoicemailDropRecording>('/dialer/voicemail-drops', {
    name,
    audioUrl,
    durationSeconds,
  });
  return data;
}

export async function deleteVoicemailDrop(id: string): Promise<void> {
  await apiClient.delete(`/dialer/voicemail-drops/${id}`);
}

export async function executeVoicemailDrop(
  recordingId: string,
  externalCallId: string,
): Promise<VoicemailDropResult> {
  const { data } = await apiClient.post<VoicemailDropResult>(
    `/dialer/voicemail-drops/${recordingId}/drop`,
    { externalCallId },
  );
  return data;
}

// -------------------------------------------------------------------------
// DID pool
// -------------------------------------------------------------------------

export async function fetchDidPool(): Promise<TenantDidPool[]> {
  const { data } = await apiClient.get<TenantDidPool[]>('/dialer/did-pool');
  return data;
}

export async function addDid(
  countryCode: string,
  phoneNumber: string,
  provider?: string,
): Promise<TenantDidPool> {
  const { data } = await apiClient.post<TenantDidPool>('/dialer/did-pool', {
    countryCode,
    phoneNumber,
    provider,
  });
  return data;
}

export async function deactivateDid(id: string): Promise<void> {
  await apiClient.delete(`/dialer/did-pool/${id}`);
}

// -------------------------------------------------------------------------
// Blended mode
// -------------------------------------------------------------------------

export async function fetchSessionTranscript(sessionId: string): Promise<SessionTranscript> {
  const { data } = await apiClient.get<SessionTranscript>(
    `/voice/sessions/${sessionId}/transcript`,
  );
  return data;
}

export async function transferToHuman(
  sessionId: string,
  repPhoneNumber: string,
): Promise<TransferToHumanResult> {
  const { data } = await apiClient.post<TransferToHumanResult>(
    `/voice/sessions/${sessionId}/transfer`,
    { repPhoneNumber },
  );
  return data;
}
